/**
 * Tokens panel — visual samples of JAKROO 2026 design tokens
 */

import * as React from 'react'
import styled from 'styled-components'
import {
  BORDER_TOKENS,
  COLOR_TOKENS,
  ColorToken,
  FONT_FAMILY_CSS,
  FONT_FAMILY_TOKENS,
  getTokenStatus,
  RADIUS_TOKENS,
  SPACING_CODE_EXTRAS,
  SPACING_TOKENS,
  TYPE_STYLE_TOKENS,
  TokenStatus,
} from './tokenRegistry'
import {
  GroupTitle,
  Legend,
  MetaCode,
  MetaRow,
  SectionSub,
  SectionTitle,
  StatusBadge,
} from './sandboxStyles'

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`

const SwatchCard = styled.div`
  border: 1px solid var(--neutral-border-light);
  background: var(--neutral-white);
  overflow: hidden;
`

const SwatchFill = styled.div<{ color: string }>`
  height: 88px;
  background: ${({ color }) => color};
  border-bottom: 1px solid var(--neutral-border-light);
`

const SwatchBody = styled.div`
  padding: 10px 12px 12px;
`

const TokenName = styled.div`
  font: 600 12px/16px var(--font-barlow);
  color: var(--neutral-darkest);
`

const SpacingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 560px;
`

const SpacingRow = styled.div`
  display: grid;
  grid-template-columns: 120px 56px 1fr;
  gap: 12px;
  align-items: center;
`

const SpacingBar = styled.div<{ size: string; faded?: boolean }>`
  height: ${({ size }) => size};
  min-height: 2px;
  max-height: 100px;
  width: 200px;
  background: ${({ faded }) =>
    faded ? 'var(--neutral-border-med)' : 'var(--neutral-darkest)'};
  opacity: ${({ faded }) => (faded ? 0.55 : 1)};
`

const RadiusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 20px;
`

const RadiusSample = styled.div<{ radius: string; faded?: boolean }>`
  width: 80px;
  height: 80px;
  background: var(--neutral-fill-lightest);
  border: 1px solid var(--neutral-border-med);
  border-radius: ${({ radius }) => radius};
  opacity: ${({ faded }) => (faded ? 0.65 : 1)};
`

const BorderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 480px;
`

const BorderLine = styled.div<{ width: string; faded?: boolean }>`
  height: 0;
  border-top: ${({ width }) => width} solid var(--neutral-darkest);
  opacity: ${({ faded }) => (faded ? 0.55 : 1)};
`

const TypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TypeRow = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid var(--neutral-border-light);
`

const TypeSpecimen = styled.div<{
  family: string
  weight: number
  size: number
  lineHeight: number
  letterSpacing: number
  textTransform: string
}>`
  font-family: ${({ family }) => family};
  font-weight: ${({ weight }) => weight};
  font-size: ${({ size }) => size}px;
  line-height: ${({ lineHeight }) => lineHeight}px;
  letter-spacing: ${({ letterSpacing }) => letterSpacing}px;
  text-transform: ${({ textTransform }) => textTransform};
  color: var(--neutral-darkest);
  margin-top: 8px;
  overflow-wrap: break-word;
`

const statusLabel = (status: TokenStatus) => {
  if (status === 'implemented') {
    return 'In code'
  }
  if (status === 'mismatch') {
    return 'Mismatch'
  }
  return 'Missing'
}

const colorPreview = (token: ColorToken, status: TokenStatus) => {
  if (status === 'missing') {
    return token.figmaValue
  }
  if (token.cssVar) {
    return `var(${token.cssVar})`
  }
  return token.figmaValue
}

const ColorGroup: React.FC<{ title: string; tokens: ColorToken[] }> = ({
  title,
  tokens,
}) => (
  <div>
    <GroupTitle>{title}</GroupTitle>
    <SwatchGrid>
      {tokens.map((token) => {
        const status = getTokenStatus(
          token.figmaValue,
          token.codeValue,
          'color'
        )
        return (
          <SwatchCard key={token.name}>
            <SwatchFill color={colorPreview(token, status)} />
            <SwatchBody>
              <TokenName>{token.name}</TokenName>
              <MetaRow>
                <StatusBadge status={status}>
                  {statusLabel(status)}
                </StatusBadge>
              </MetaRow>
              <MetaRow>
                {token.cssVar ? (
                  <MetaCode>{token.cssVar}</MetaCode>
                ) : (
                  <MetaCode>no CSS var</MetaCode>
                )}
              </MetaRow>
              <MetaRow>
                <MetaCode>
                  Figma {token.figmaValue}
                  {status === 'mismatch' && token.codeValue
                    ? ` · code ${token.codeValue}`
                    : ''}
                </MetaCode>
              </MetaRow>
              {token.note && (
                <MetaRow>
                  <MetaCode style={{ color: 'var(--neutral-secondary)' }}>
                    {token.note}
                  </MetaCode>
                </MetaRow>
              )}
            </SwatchBody>
          </SwatchCard>
        )
      })}
    </SwatchGrid>
  </div>
)

const TokensPanel: React.FC = () => {
  const neutrals = COLOR_TOKENS.filter((t) => t.group === 'neutral')
  const series = COLOR_TOKENS.filter((t) => t.group === 'series')
  const accents = COLOR_TOKENS.filter((t) => t.group === 'accent')

  return (
    <div>
      <Legend>
        <StatusBadge status="implemented">In code</StatusBadge>
        <StatusBadge status="mismatch">Mismatch</StatusBadge>
        <StatusBadge status="missing">Missing</StatusBadge>
      </Legend>
      <SectionSub>
        Samples use live CSS variables when available. Missing tokens show the
        Figma expected value as a static fallback. Mismatch means the var exists
        but the value differs from Figma.
      </SectionSub>

      <SectionTitle>Colors</SectionTitle>
      <ColorGroup title="Neutral" tokens={neutrals} />
      <ColorGroup title="Series" tokens={series} />
      <ColorGroup title="Accent" tokens={accents} />

      <SectionTitle>Spacing</SectionTitle>
      <SectionSub>
        Figma scale first; code-only extras listed below. Bars use the CSS var
        when present.
      </SectionSub>
      <SpacingList>
        {SPACING_TOKENS.map((token) => {
          const status =
            token.figmaValue === '—'
              ? 'implemented'
              : getTokenStatus(token.figmaValue, token.codeValue, 'length')
          const size =
            status === 'missing'
              ? token.figmaValue
              : token.cssVar
                ? `var(${token.cssVar})`
                : token.figmaValue
          return (
            <div key={token.name}>
              <SpacingRow>
                <TokenName>{token.name}</TokenName>
                <MetaCode>
                  {status === 'missing' ? token.figmaValue : token.codeValue}
                </MetaCode>
                <SpacingBar size={size} faded={status === 'missing'} />
              </SpacingRow>
              <MetaRow>
                <StatusBadge status={status}>{statusLabel(status)}</StatusBadge>
                {token.cssVar && <MetaCode>{token.cssVar}</MetaCode>}
                {token.note && (
                  <MetaCode style={{ color: 'var(--neutral-secondary)' }}>
                    {token.note}
                  </MetaCode>
                )}
              </MetaRow>
            </div>
          )
        })}
      </SpacingList>

      <GroupTitle>Code-only spacing</GroupTitle>
      <SpacingList>
        {SPACING_CODE_EXTRAS.map((token) => (
          <div key={token.name}>
            <SpacingRow>
              <TokenName>{token.name}</TokenName>
              <MetaCode>{token.codeValue}</MetaCode>
              <SpacingBar
                size={token.cssVar ? `var(${token.cssVar})` : token.codeValue!}
              />
            </SpacingRow>
            <MetaRow>
              <StatusBadge status="implemented">In code</StatusBadge>
              {token.cssVar && <MetaCode>{token.cssVar}</MetaCode>}
              {token.note && (
                <MetaCode style={{ color: 'var(--neutral-secondary)' }}>
                  {token.note}
                </MetaCode>
              )}
            </MetaRow>
          </div>
        ))}
      </SpacingList>

      <SectionTitle>Radius</SectionTitle>
      <SectionSub>
        Not defined as CSS variables in <code>base.less</code> yet — samples use
        Figma values as static fallbacks.
      </SectionSub>
      <RadiusGrid>
        {RADIUS_TOKENS.map((token) => {
          const status = getTokenStatus(token.figmaValue, token.codeValue, 'length')
          return (
            <div key={token.name}>
              <RadiusSample
                radius={token.figmaValue}
                faded={status === 'missing'}
              />
              <MetaRow style={{ marginTop: 8 }}>
                <TokenName>{token.name}</TokenName>
              </MetaRow>
              <MetaRow>
                <StatusBadge status={status}>{statusLabel(status)}</StatusBadge>
                <MetaCode>{token.figmaValue}</MetaCode>
              </MetaRow>
              {token.note && (
                <MetaRow>
                  <MetaCode style={{ color: 'var(--neutral-secondary)' }}>
                    {token.note}
                  </MetaCode>
                </MetaRow>
              )}
            </div>
          )
        })}
      </RadiusGrid>

      <SectionTitle>Border width</SectionTitle>
      <BorderList>
        {BORDER_TOKENS.map((token) => {
          const status = getTokenStatus(token.figmaValue, token.codeValue, 'length')
          const width =
            status === 'missing'
              ? token.figmaValue
              : token.cssVar
                ? `var(${token.cssVar})`
                : token.figmaValue
          return (
            <div key={token.name}>
              <BorderLine width={width} faded={status === 'missing'} />
              <MetaRow>
                <TokenName>{token.name}</TokenName>
                <StatusBadge status={status}>{statusLabel(status)}</StatusBadge>
                {token.cssVar ? (
                  <MetaCode>{token.cssVar}</MetaCode>
                ) : (
                  <MetaCode>{token.figmaValue}</MetaCode>
                )}
              </MetaRow>
            </div>
          )
        })}
      </BorderList>

      <SectionTitle>Font families</SectionTitle>
      <SwatchGrid>
        {FONT_FAMILY_TOKENS.map((token) => (
          <SwatchCard key={token.name}>
            <SwatchBody>
              <div
                style={{
                  fontFamily: `var(${token.cssVar})`,
                  fontWeight: 600,
                  fontSize: 22,
                  lineHeight: '28px',
                  marginBottom: 8,
                  color: 'var(--neutral-darkest)',
                }}
              >
                Aa
              </div>
              <TokenName>{token.name}</TokenName>
              <MetaRow>
                <StatusBadge status="implemented">In code</StatusBadge>
              </MetaRow>
              <MetaRow>
                <MetaCode>{token.cssVar}</MetaCode>
              </MetaRow>
            </SwatchBody>
          </SwatchCard>
        ))}
      </SwatchGrid>

      <SectionTitle>Typography styles</SectionTitle>
      <SectionSub>
        Specimens apply Figma size/weight/tracking with live{' '}
        <code>--font-*</code> families. Dedicated text-style CSS variables are
        not defined yet.
      </SectionSub>
      <TypeList>
        {TYPE_STYLE_TOKENS.map((token) => (
          <TypeRow key={token.name}>
            <MetaRow>
              <TokenName>{token.name}</TokenName>
              <StatusBadge status="missing">Style token missing</StatusBadge>
              <MetaCode>
                {token.weight} · {token.size}/{token.lineHeight}/
                {token.letterSpacing}px
              </MetaCode>
            </MetaRow>
            {token.note && (
              <MetaRow>
                <MetaCode style={{ color: 'var(--neutral-secondary)' }}>
                  {token.note}
                </MetaCode>
              </MetaRow>
            )}
            <TypeSpecimen
              family={FONT_FAMILY_CSS[token.family]}
              weight={token.weight}
              size={Math.min(token.size, 48)}
              lineHeight={Math.min(token.lineHeight, 52)}
              letterSpacing={token.letterSpacing}
              textTransform={token.textTransform || 'none'}
            >
              {token.size > 48
                ? `${token.name} (preview capped at 48px; Figma ${token.size}px)`
                : 'The quick brown fox jumps over the lazy dog'}
            </TypeSpecimen>
          </TypeRow>
        ))}
      </TypeList>
    </div>
  )
}

export default TokensPanel



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/theme/sandbox/TokensPanel.tsx